import { createContext, useContext, useMemo, useState, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { api, ApiClientError } from "./api";

interface AuthModalState {
  returnTo: string;
  onBeforeRedirect?: () => void;
}

interface AuthContextValue {
  me: ReturnType<typeof useCurrentUser>["data"];
  isLoading: boolean;
  modal: AuthModalState | null;
  requestLogin(returnTo?: string, onBeforeRedirect?: () => void): void;
  closeLogin(): void;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function useCurrentUser() {
  return useQuery({
    queryKey: ["me"],
    queryFn: async () => {
      try {
        return await api.me();
      } catch (error) {
        if (error instanceof ApiClientError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 20_000,
  });
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const currentUser = useCurrentUser();
  const [modal, setModal] = useState<AuthModalState | null>(null);
  const value = useMemo<AuthContextValue>(
    () => ({
      me: currentUser.data,
      isLoading: currentUser.isLoading,
      modal,
      requestLogin: (returnTo = "/generate", onBeforeRedirect) => setModal({ returnTo, onBeforeRedirect }),
      closeLogin: () => setModal(null),
    }),
    [currentUser.data, currentUser.isLoading, modal],
  );
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used inside AuthProvider");
  return context;
}
